import type { OnInit, OnDestroy } from '@angular/core';
import { Directive, ElementRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Subscription, fromEvent } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

@Directive({
    selector: '[webRotationHandler]',
})
export class RotationHandlerDirective implements OnInit, OnDestroy {
    private readonly elementRef = inject(ElementRef);
    private readonly document = inject(DOCUMENT);

    @Input() public targetElement!: HTMLElement;
    @Input() public rotation = 0;

    @Output() public rotationChangeStart = new EventEmitter<void>();
    @Output() public rotationChange = new EventEmitter<number>();
    @Output() public rotationChangeEnd = new EventEmitter<void>();

    private startSub!: Subscription;
    private dragSub = new Subscription();

    private center = { x: 0, y: 0 };
    private rotationStart = 0;
    private angleStart = 0;
    private lastEmitted = 0;

    public ngOnInit(): void {
        if (!(this.targetElement instanceof HTMLElement)) {
            console.warn('Rotation handler: target element is not set or invalid.');
            return;
        }

        const dragStart$ = fromEvent<MouseEvent>(this.elementRef.nativeElement, 'mousedown');
        const dragMove$ = fromEvent<MouseEvent>(this.document, 'mousemove');
        const dragEnd$ = fromEvent<MouseEvent>(this.document, 'mouseup');

        this.startSub = dragStart$.subscribe((event: MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();

            const rect = this.targetElement.getBoundingClientRect();
            this.center = {
                x: rect.left + rect.width / 2,
                y: rect.top + rect.height / 2,
            };

            this.angleStart = this.calculateAngle(event.clientX, event.clientY);
            this.rotationStart = this.rotation;
            this.lastEmitted = Math.round(this.rotation);

            this.rotationChangeStart.emit();

            this.dragSub.unsubscribe();
            this.dragSub = new Subscription();

            this.dragSub.add(
                dragMove$.pipe(takeUntil(dragEnd$)).subscribe((moveEvent: MouseEvent) => {
                    const angleNow = this.calculateAngle(moveEvent.clientX, moveEvent.clientY);
                    let delta = angleNow - this.angleStart;

                    if (delta > 180) delta -= 360;
                    if (delta < -180) delta += 360;

                    const newRotation = Math.round(this.rotationStart + delta);

                    if (newRotation !== this.lastEmitted) {
                        this.lastEmitted = newRotation;
                        this.rotationChange.emit(newRotation);
                    }
                })
            );

            this.dragSub.add(
                dragEnd$.pipe(take(1)).subscribe(() => {
                    this.rotationChangeEnd.emit();
                })
            );
        });
    }

    public ngOnDestroy(): void {
        this.startSub?.unsubscribe();
        this.dragSub.unsubscribe();
    }

    private calculateAngle(x: number, y: number): number {
        const dx = x - this.center.x;
        const dy = y - this.center.y;
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);
        return angle < 0 ? angle + 360 : angle;
    }
}
